# ATS Checker Service

No-signup ATS resume scoring backend. Accepts pre-parsed resume text and metadata, runs LLM-based analysis (Gemini 2.5 Flash), and returns a structured score report.

---

## Endpoint

```
POST /v1/anon/ats-check
```

- **Auth**: None (anonymous)
- **CORS**: Enabled

## Headers

| Header | Required | Description |
|--------|----------|-------------|
| `X-Recaptcha` | ✅ | reCAPTCHA Enterprise token. Action name: `ats_check` |
| `Content-Type` | ✅ | `application/json` |

## Rate Limiting

- **Limit**: 10 requests per IP per day
- **Storage**: Firestore collection `public_api_rate_limits`, doc ID: `{ip}_{YYYY-MM-DD}`
- **IP resolution priority**: `CF-Connecting-IP` → `X-Forwarded-For` → `RemoteIP`
- **Fail-open**: if Firestore is unavailable, requests are allowed through

---

## Request

```json
{
  "resumeText":      "string (required) — extracted plain text from the resume",
  "fileName":        "string — original file name (e.g. resume.pdf)",
  "fileSize":        123456,
  "isImagePdf":      false,
  "isComplexLayout": false,
  "jobDescription":  "string (optional) — job description text for keyword matching"
}
```

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| `resumeText` | string | ✅ | Plain text extracted from resume file |
| `fileName` | string | | Original file name |
| `fileSize` | int64 | | File size in bytes |
| `isImagePdf` | bool | | `true` if file is image-based (non-extractable) |
| `isComplexLayout` | bool | | `true` if multi-column / table-heavy layout detected |
| `jobDescription` | string | | Triggers JD keyword matching mode when **≥ 50 words** |

---

## Response

```json
{
  "totalScore": 75,
  "grade": "needs_improvement",
  "gradeLabel": "Needs Improvement",
  "formatting": {
    "score": 40,
    "maxScore": 50,
    "checks": [ { "id": "...", "name": "...", "description": "...", "status": "fail", "penalty": 10, "detail": "..." } ]
  },
  "structure": {
    "score": 20,
    "maxScore": 30,
    "checks": [...]
  },
  "keywords": {
    "score": 15,
    "maxScore": 20,
    "mode": "job_description",
    "checks": [...],
    "matchedKeywords": ["python", "aws"],
    "missingKeywords": ["kubernetes", "terraform"],
    "matchPercentage": 60
  },
  "topIssues": [...]
}
```

## Response Codes

| Status | Body | Meaning |
|--------|------|---------|
| `200 OK` | `AnalysisResult` | Success |
| `400 Bad Request` | `{"error": "..."}` | Missing required field (`resumeText`) |
| `403 Forbidden` | `{"error": "reCAPTCHA token required"}` | `X-Recaptcha` header missing |
| `403 Forbidden` | `{"error": "reCAPTCHA verification failed"}` | Invalid/expired token or score < 0.7 |
| `429 Too Many Requests` | `{"error": "Daily limit reached", "limit": true}` | IP exceeded 10 requests/day |

---

### `grade` values

| Value | Score Range |
|-------|------------|
| `ats_ready` | 80 – 100 |
| `needs_improvement` | 50 – 79 |
| `critical` | 0 – 49 |

### `status` values per check

| Value | Meaning |
|-------|---------|
| `pass` | Check passed, no penalty |
| `warning` | Minor issue, penalty applied |
| `fail` | Issue found, penalty applied |

---

## Scoring Logic

Total score = **Formatting (50)** + **Structure (30)** + **Keywords (20)** = 100

---

### A. Formatting — 50 points

Checks whether the resume file is physically readable by ATS systems.

#### 1. Text Extractability

| | |
|--|--|
| **ID** | `text-extractable` |
| **Penalty** | –50 (immediate score = 0) |
| **Fail condition** | `isImagePdf: true` or `resumeText` is empty |

| Status | description | detail |
|--------|-------------|--------|
| ❌ fail | `"Unreadable File"` | `"This resume appears to be an image-based PDF or corrupted file. ATS systems cannot extract text from it — it will be scored as a blank resume."` |
| ✅ pass | `"Text is machine-readable"` | `"All text content is extractable by ATS parsers."` |

> If this check fails, the remaining checks are skipped and the total formatting score is 0.

---

#### 2. File Size

| | |
|--|--|
| **ID** | `file-size` |
| **Penalty** | –10 |
| **Fail condition** | `fileSize > 5 MB (5,242,880 bytes)` |

| Status | description | detail |
|--------|-------------|--------|
| ❌ fail | `"File size exceeds 5 MB limit"` | `"Your file is {X.X} MB. Files over 5 MB may be rejected or time out in ATS systems. Compress images or remove embedded assets."` |
| ✅ pass | `"File size is within the recommended limit"` | `"Your file is {X.X} MB — well within the 5 MB ATS limit."` |

---

#### 3. Layout Compatibility

| | |
|--|--|
| **ID** | `layout-complexity` |
| **Penalty** | –20 |
| **Fail condition** | `isComplexLayout: true` |

| Status | description | detail |
|--------|-------------|--------|
| ❌ fail | `"Complex layout detected"` | `"Multi-column layouts, text boxes, or graphic elements were detected. ATS parsers read text linearly — these elements cause content to be misread or skipped entirely. Use a single-column format."` |
| ✅ pass | `"Layout is ATS-compatible"` | `"No complex layout elements detected. Single-column format is fully readable by ATS parsers."` |

---

### B. Structure — 30 points

Checks whether the resume contains the required sections and contact information.

#### 4. Section Headers

| | |
|--|--|
| **ID** | `section-headers` |
| **Penalty** | –10 |
| **Fail condition** | "Work Experience" or "Education" section header missing |

Detected by substring match (case-insensitive):
- **Experience**: `work experience`, `professional experience`, `employment history`, `work history`, `career history`, `relevant experience`, `professional history`, `work background`, or standalone `experience` header on its own line
- **Education**: `academic background`, `academic history`, `educational background`, `academic credentials`, `education and training`, or standalone `education` header on its own line

| Status | description | detail |
|--------|-------------|--------|
| ❌ fail (both missing) | `"Required sections are missing"` | `"Neither a Work Experience nor an Education section was found..."` |
| ⚠️ warning (one missing) | `"A required section is missing"` | `"Missing: {section}. Found: {section}..."` |
| ✅ pass | `"Required sections are present"` | `"Found: Work Experience, Education."` |

---

#### 5. Skills Section

| | |
|--|--|
| **ID** | `skills-section` |
| **Penalty** | –10 |
| **Fail condition** | No skills section header found |

Detected by: `technical skills`, `core competencies`, `key skills`, `professional skills`, `skills and abilities`, `areas of expertise`, or standalone `skills` header on its own line

| Status | description | detail |
|--------|-------------|--------|
| ⚠️ warning | `"No skills section found"` | `"Add a dedicated "Skills" section..."` |
| ✅ pass | `"Skills section is present"` | `"Skills section found — ATS can match your skills against job requirements."` |

---

#### 6. Contact Information

| | |
|--|--|
| **ID** | `contact-info` |
| **Penalty** | –10 |
| **Fail condition** | No email address found (regex match) |

| Status | description | detail |
|--------|-------------|--------|
| ❌ fail | `"Email address not found"` | `"No email address was detected..."` |
| ✅ pass | `"Contact information is present"` | `"Email: found. Phone: {found / not found (optional)}."` |

---

### C. Keywords — 20 points

Keyword analysis is powered by **Gemini 2.5 Flash** when `GEMINI_KEY` is set.
Falls back to the rule-based engine when Gemini is unavailable or returns an error.

Two modes depending on whether a job description is provided.

---

#### Mode A: Job Description provided (`jobDescription` ≥ 50 words)

**Check ID**: `jd-keyword-match`

**Gemini analysis** (primary):
- Extracts the top 10–15 important keywords (technical skills, tools, certifications, domain terms) from the JD
- Determines which keywords are present in the resume, understanding synonyms and abbreviations (e.g. `"JS"` = `"JavaScript"`, `"AWS"` = `"Amazon Web Services"`)
- Returns `matchedKeywords` and `missingKeywords` arrays

**Rule-based fallback**:
- Expands both JD and resume text with synonym variants
- Extracts top 10 keywords from JD using TF (term frequency), bigrams prioritized
- Counts how many extracted keywords appear in the expanded resume text

**Scoring** (same for both):
- `matchPct = matched / (matched + missing) × 100`

| Match % | Penalty | Status | description | detail |
|---------|---------|--------|-------------|--------|
| < 20% | –20 | ❌ fail | `"{N}% keyword match — critical mismatch"` | `"Only {N} of {total} key terms from the job description were found..."` |
| 20–49% | –10 | ❌ fail | `"{N}% keyword match — below threshold"` | `"{N} of {total} key terms matched. Missing: {keyword, ...}."` |
| ≥ 50% | 0 | ✅ pass | `"{N}% keyword match"` | `"{N} of {total} key terms from the job description were found."` |

Response also includes `matchedKeywords` and `missingKeywords` arrays.

---

#### Mode B: No Job Description (default, < 50 words)

**Check 1 — Action Verbs** (`action-verbs`)

**Gemini analysis** (primary): counts distinct strong action verbs (e.g. `led`, `developed`, `optimized`), ignoring weak verbs like `helped`, `worked`, `did`. Returns count and example verbs.

**Rule-based fallback**: matches against a list of ~60 predefined power verbs. Minimum threshold: 3.

| Verbs found | Penalty | Status | description | detail |
|-------------|---------|--------|-------------|--------|
| 0 | –10 | ❌ fail | `"No action verbs found"` | `"No strong action verbs were detected..."` |
| 1–2 | –10 | ⚠️ warning | `"Too few action verbs"` | `"Only {N} action verb(s) found: {verb, ...}. Aim for at least 3."` |
| 3+ | 0 | ✅ pass | `"Strong action verbs detected"` | `"Found {N} action verbs: {verb, ...}."` |

**Check 2 — Quantifiable Metrics** (`metrics`)

**Gemini analysis** (primary): detects quantifiable metrics including percentages, dollar amounts, multipliers, and large numbers. Returns boolean and example values.

**Rule-based fallback**: regex detection:
- Percentages: `40%`, `12.5%`
- Dollar amounts: `$5K`, `$1.2M`, `$500,000`
- Multipliers: `2x`, `3.5x`
- Named numbers: `1 million`, `500 thousand`
- Large comma-separated numbers: `1,000+`

| | Penalty | Status | description | detail |
|-|---------|--------|-------------|--------|
| None found | –10 | ❌ fail | `"No measurable achievements found"` | `"No numbers, percentages, or dollar amounts were detected..."` |
| Found | 0 | ✅ pass | `"Measurable achievements detected"` | `"Metrics found: {examples}. Strong signal for ATS keyword scoring."` |

---

## Gemini Integration

| Setting | Value |
|---------|-------|
| Model | `gemini-2.5-flash` |
| Temperature | `0.0` |
| Max output tokens | `1024` |
| Response format | JSON (structured schema) |

**Fallback behavior:**

| Situation | Behavior |
|-----------|----------|
| `GEMINI_KEY` not set | `gemini = nil` — always uses rule-based |
| Gemini API error | Logs error, falls back to rule-based |
| Empty/unparseable response | Logs error, falls back to rule-based |

---

## Top Issues

The response includes a `topIssues` array — up to 5 failed/warning checks sorted by penalty (highest first). Use this to surface the most impactful problems in the UI.

---

## File Structure

```
apps/micro-services/ats-checker/
├── cmd/
│   └── main.go                      # Entry point
├── config/
│   └── dev.env
├── pkg/
│   ├── api/
│   │   ├── controllers/
│   │   │   └── ats_checker.go       # HTTP handler
│   │   └── requests/
│   │       └── ats_check.go         # Request type
│   ├── routes/
│   │   └── routes.go                # Route registration
│   └── services/
│       ├── types.go                 # Shared types
│       ├── keywords.go              # Action verbs, stop words, synonym map (rule-based fallback data)
│       ├── format_checker.go        # Formatting checks (50 pts)
│       ├── structure_checker.go     # Structure checks (30 pts)
│       ├── keyword_checker.go       # Keyword checks (20 pts) — Gemini-first + rule-based fallback
│       └── analyzer.go              # Orchestrator
├── go.mod / go.sum
└── Dockerfile
```
